import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d?.getTime())) return '—';
  return d?.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
};

const PendingOrdersTab = () => {
  const { user } = useAuth();
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelModal, setCancelModal] = useState({ open: false, orderId: null });
  const [cancelling, setCancelling] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [preset, setPreset] = useState('this_fy');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const loadPendingOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const userEmail = session?.user?.email || user?.email;
      const userId = session?.user?.id || user?.id;

      if (!userEmail && !userId) {
        setPendingOrders([]);
        return;
      }

      // Fetch both 'pending' and 'pending_approval' bookings for this buyer
      const { data: bookings, error } = await supabase
        ?.from('buyer_bookings')
        ?.select('*, readable_offer_id, readable_booking_id')
        ?.in('status', ['pending', 'pending_approval'])
        ?.order('booking_date', { ascending: false });

      if (error) throw error;

      // Filter to this buyer's bookings
      const filtered = (bookings || [])?.filter(b =>
        b?.buyer_email === userEmail ||
        b?.buyer_id === userId
      );

      const bookingOrders = filtered?.map(b => ({
        _source: 'supabase',
        id: b?.id,
        factoryName: b?.factory_name || '—',
        poNumber: b?.readable_offer_id || b?.readable_booking_id || b?.offer_id || b?.id?.toString()?.slice(0, 8)?.toUpperCase(),
        readableOfferId: b?.readable_offer_id || null,
        readableBookingId: b?.readable_booking_id || null,
        sugarGrade: b?.grade || '—',
        qtyRequired: b?.quantity || 0,
        totalAmount: b?.total_amount || 0,
        pricePerTon: b?.rate_per_ton || 0,
        deliveryDate: b?.booking_date || null,
        deliveryDestination: b?.loading_from || '—',
        submittedAt: b?.booking_date || b?.created_at || new Date()?.toISOString(),
        status: b?.status === 'pending_approval' ? 'Pending for Approval' : 'Pending for Acceptance',
        rawStatus: b?.status,
        bookingType: b?.booking_type || 'sugar_only',
        noOfTrucks: b?.no_of_trucks || 0,
        destination: b?.destination || '',
        quotaId: b?.quota_id || null,
        orderType: 'buy',
      }));

      // Also fetch sell offers submitted by this buyer (from quotas table)
      let sellOfferOrders = [];
      if (userEmail) {
        const { data: sellOffers, error: sellError } = await supabase
          ?.from('quotas')
          ?.select('*')
          ?.eq('seller_email', userEmail)
          ?.in('availability_status', ['reserved', 'pending_approval'])
          ?.order('created_at', { ascending: false });

        if (!sellError && sellOffers?.length > 0) {
          sellOfferOrders = sellOffers?.map(q => ({
            _source: 'quotas',
            id: `sell-${q?.id}`,
            factoryName: q?.factory_name || '—',
            poNumber: q?.offer_id || q?.id?.toString()?.slice(0, 8)?.toUpperCase(),
            readableOfferId: q?.offer_id || null,
            readableBookingId: null,
            sugarGrade: q?.grade || '—',
            qtyRequired: parseFloat(q?.quantity) || 0,
            totalAmount: (parseFloat(q?.quantity) || 0) * (parseFloat(q?.price_per_ton) || 0),
            pricePerTon: parseFloat(q?.price_per_ton) || 0,
            deliveryDate: q?.available_from_date || null,
            deliveryDestination: q?.factory_name || '—',
            submittedAt: q?.created_at || new Date()?.toISOString(),
            status: 'Pending for Approval',
            rawStatus: q?.availability_status,
            bookingType: 'sell_offer',
            noOfTrucks: 0,
            destination: '',
            quotaId: q?.id,
            orderType: 'sell',
          }));
        }
      }

      setPendingOrders([...bookingOrders, ...sellOfferOrders]);
    } catch (err) {
      console.error('PendingOrdersTab error:', err?.message);
      setPendingOrders([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadPendingOrders();

    const channel = supabase?.channel('pending_orders_tab')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'buyer_bookings' }, () => {
        loadPendingOrders();
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  }, [loadPendingOrders]);

  const handleCancelBooking = async () => {
    if (!cancelModal?.orderId) return;
    setCancelling(true);
    try {
      const { error } = await supabase
        ?.from('buyer_bookings')
        ?.update({ status: 'cancelled' })
        ?.eq('id', cancelModal?.orderId);

      if (error) throw error;

      // Reload orders after cancellation
      await loadPendingOrders();
    } catch (err) {
      console.error('Cancel booking error:', err?.message);
    } finally {
      setCancelling(false);
      setCancelModal({ open: false, orderId: null });
    }
  };

  // Filter logic
  const { from: dateFrom, to: dateTo } = getDateRange(preset, customFrom, customTo);

  const filteredOrders = pendingOrders?.filter(order => {
    const s = searchText?.toLowerCase() || '';
    const matchesSearch = !s ||
      order?.factoryName?.toLowerCase()?.includes(s) ||
      order?.sugarGrade?.toLowerCase()?.includes(s) ||
      order?.readableOfferId?.toLowerCase()?.includes(s) ||
      order?.readableBookingId?.toLowerCase()?.includes(s) ||
      order?.poNumber?.toLowerCase()?.includes(s);

    const orderDate = order?.submittedAt ? new Date(order.submittedAt) : null;
    const matchesDate =
      (!dateFrom && !dateTo) ||
      (orderDate &&
        (!dateFrom || orderDate >= dateFrom) &&
        (!dateTo || orderDate <= dateTo));

    return matchesSearch && matchesDate;
  });

  return (
    <div className="space-y-6">
      {/* Cancel Confirmation Modal */}
      {cancelModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center" style={{ backgroundColor: '#ffffff' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fef2f2' }}>
              <Icon name="AlertTriangle" size={26} color="#dc2626" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#1f2937' }}>Cancel Booking?</h3>
            <p className="text-sm mb-6" style={{ color: '#6b7280' }}>
              Are you sure you want to cancel this booking? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setCancelModal({ open: false, orderId: null })}
                disabled={cancelling}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold border-2 transition-all"
                style={{ borderColor: '#d1d5db', color: '#374151', backgroundColor: '#ffffff' }}
              >
                Keep Booking
              </button>
              <button
                onClick={handleCancelBooking}
                disabled={cancelling}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-all flex items-center justify-center gap-2"
                style={{ backgroundColor: '#dc2626' }}
              >
                {cancelling ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Cancelling...
                  </>
                ) : 'Cancel Booking'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: '#1e293b' }}>Pending Orders</h2>
          <p className="text-sm mt-0.5" style={{ color: '#374151' }}>
            {loading ? 'Loading...' : `${filteredOrders?.length} order${filteredOrders?.length !== 1 ? 's' : ''} awaiting approval`}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
          style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
          <Icon name="Clock" size={15} color="#d97706" />
          Quota Manager Review
        </div>
      </div>

      {/* Info Banner */}
      <div className="rounded-xl px-5 py-4 flex items-start gap-3"
        style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
        <Icon name="Info" size={18} color="#d97706" />
        <p className="text-sm" style={{ color: '#92400e' }}>
          Your bookings have been submitted and are pending approval. You will be notified once approved.
        </p>
      </div>

      {/* Search + Date Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <Icon name="Search" size={16} color="#9ca3af" />
          </span>
          <input
            type="text"
            placeholder="Search by factory, grade, or booking ID..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none"
            style={{ border: '1px solid #e5e7eb', color: '#1e293b', backgroundColor: '#ffffff' }}
          />
        </div>
        <DateRangeFilter
          preset={preset}
          onPresetChange={setPreset}
          customFrom={customFrom}
          onCustomFromChange={setCustomFrom}
          customTo={customTo}
          onCustomToChange={setCustomTo}
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
          <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm" style={{ color: '#9ca3af' }}>Loading pending orders...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredOrders?.length === 0 && (
        <div className="text-center py-16 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
          <Icon name="ClipboardList" size={40} color="#d1a87a" />
          <p className="mt-3 text-sm font-medium" style={{ color: '#9ca3af' }}>No pending orders at this time</p>
          <p className="text-xs mt-1" style={{ color: '#d1a87a' }}>Bookings submitted for quota approval will appear here</p>
        </div>
      )}

      {/* Orders List */}
      {!loading && filteredOrders?.length > 0 && (
        <div className="space-y-4">
          {filteredOrders?.map((order) => (
            <div
              key={order?.id}
              className="rounded-xl p-5 transition-all duration-200"
              style={{
                background: '#ffffff',
                border: '1px solid #fde68a',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}
            >
              {/* Top Row */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: '#fef3c7' }}>
                    <Icon name="ClipboardList" size={20} color="#d97706" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base leading-tight" style={{ color: '#1e293b' }}>
                      {order?.factoryName}
                    </h3>
                    {/* Order Ref — prominent readable ID */}
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-xs font-medium" style={{ color: '#9ca3af' }}>Order Ref:</span>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold tracking-wide"
                        style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}
                      >
                        <Icon name="Hash" size={11} color="#c2410c" />
                        {order?.readableOfferId || order?.poNumber}
                      </span>
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>
                      {formatDate(order?.submittedAt)}
                    </p>
                  </div>
                </div>
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold self-start sm:self-auto"
                  style={{ background: '#fef3c7', color: '#92400e' }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#f59e0b' }} />
                  {order?.status}
                </span>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg px-3 py-2.5" style={{ background: '#f3ede3' }}>
                  <p className="text-xs mb-1" style={{ color: '#9ca3af' }}>Sugar Grade</p>
                  <p className="text-sm font-semibold" style={{ color: '#374151' }}>{order?.sugarGrade}</p>
                </div>
                <div className="rounded-lg px-3 py-2.5" style={{ background: '#f3ede3' }}>
                  <p className="text-xs mb-1" style={{ color: '#9ca3af' }}>Quantity</p>
                  <p className="text-sm font-semibold" style={{ color: '#1e293b' }}>{order?.qtyRequired} Tons</p>
                </div>
                <div className="rounded-lg px-3 py-2.5" style={{ background: '#f3ede3' }}>
                  <p className="text-xs mb-1" style={{ color: '#9ca3af' }}>Price/Ton</p>
                  <p className="text-sm font-semibold" style={{ color: '#d97706' }}>
                    {order?.pricePerTon ? `₹${parseFloat(order?.pricePerTon)?.toLocaleString('en-IN')}` : '—'}
                  </p>
                </div>
                <div className="rounded-lg px-3 py-2.5" style={{ background: '#f3ede3' }}>
                  <p className="text-xs mb-1" style={{ color: '#9ca3af' }}>Booked On</p>
                  <p className="text-sm font-medium" style={{ color: '#374151' }}>
                    {formatDate(order?.submittedAt)}
                  </p>
                </div>
              </div>

              {/* Destination & Trucks */}
              <div className="flex flex-wrap items-center gap-4 text-xs mb-4" style={{ color: '#9ca3af' }}>
                <div className="flex items-center gap-2">
                  <Icon name="MapPin" size={13} color="#9ca3af" />
                  <span>Loading: <span style={{ color: '#374151' }}>{order?.deliveryDestination}</span></span>
                </div>
                {order?.bookingType === 'sugar_truck' && order?.noOfTrucks > 0 && (
                  <div className="flex items-center gap-2">
                    <Icon name="Truck" size={13} color="#9ca3af" />
                    <span>Trucks: <span style={{ color: '#374151' }}>{order?.noOfTrucks}</span></span>
                  </div>
                )}
                {order?.totalAmount > 0 && (
                  <div className="flex items-center gap-2">
                    <Icon name="IndianRupee" size={13} color="#9ca3af" />
                    <span>Total: <span className="font-semibold" style={{ color: '#d97706' }}>₹{Number(order?.totalAmount)?.toLocaleString('en-IN')}</span></span>
                  </div>
                )}
              </div>

              {/* Cancel Button */}
              <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: '#fde68a' }}>
                <div className="flex items-center gap-2">
                  <Icon name="Clock" size={14} color="#d97706" />
                  <p className="text-xs" style={{ color: '#92400e' }}>
                    Pending approval from Quota Manager
                  </p>
                </div>
                <button
                  onClick={() => setCancelModal({ open: true, orderId: order?.id })}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
                  style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
                >
                  <Icon name="X" size={13} color="#dc2626" />
                  Cancel Booking
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingOrdersTab;
